/**
 * Banner shown above the action list when one or more current-version
 * actions on the proposal landed in an `indeterminate` state.
 *
 * Indeterminate means the worker crashed between firing a side effect
 * (Gmail send, calendar create, etc.) and recording the result — the
 * system cannot tell whether the side effect committed. The user must
 * verify externally and report the outcome here.
 *
 * Without this affordance, an indeterminate action permanently bricks
 * the proposal (sticky-failed dispatch in the executor, see UX expert
 * findings #2/#5/#7 on PR #5709).
 */

import { useMutation } from '@tanstack/react-query';
import { AlertTriangle } from '@untitledui/icons';
import React, { useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type {
  ExecutionResult,
  ProposedAction,
} from '@/lib/proposals/core/types';

type ResolveOutcome = 'verified_sent' | 'verified_not_sent';

interface IndeterminateResolveBannerProps {
  orgId: number;
  proposalId: number;
  actions: ProposedAction[];
  /**
   * Called after the resolve endpoint succeeds for any action so the
   * parent can refresh the proposal (which re-fetches action state and
   * re-renders the action list with the new status).
   */
  onResolved: () => void;
}

interface IndeterminateRowProps {
  orgId: number;
  proposalId: number;
  action: ProposedAction;
  onResolved: () => void;
}

// Per-target user-facing label for the verification step. Hoisted to
// module scope per FRONTEND_PATTERNS § Hoist Constants Out of Components.
const VERIFICATION_TARGET_LABELS: Record<string, string> = {
  gmail_sent_folder: 'Gmail sent folder',
  outlook_sent_folder: 'Outlook sent folder',
  google_calendar: 'Google Calendar',
  outlook_calendar: 'Outlook Calendar',
  onboarding_settings: 'Onboarding settings',
  manual_verification: 'the affected system',
};

function getVerificationLabel(
  result: ExecutionResult | null | undefined
): string {
  const target = result?.verification_target;
  if (!target) {
    return VERIFICATION_TARGET_LABELS.manual_verification;
  }
  return VERIFICATION_TARGET_LABELS[target] ?? target;
}

function IndeterminateActionRow({
  orgId,
  proposalId,
  action,
  onResolved,
}: IndeterminateRowProps) {
  const result = action.execution_result;
  const verificationLabel = getVerificationLabel(result);

  const resolveMutation = useMutation({
    mutationFn: async (outcome: ResolveOutcome) => {
      return apiPost<ProposedAction>(
        `/api/orgs/${orgId}/proposals/${proposalId}/actions/${action.id}/resolve_indeterminate/`,
        { outcome }
      );
    },
    onSuccess: () => onResolved(),
    onError: error => {
      logger.error('Failed to resolve indeterminate action', error, {
        actionId: action.id,
      });
    },
  });

  const handleMarkSent = useCallback(() => {
    resolveMutation.mutate('verified_sent');
  }, [resolveMutation]);
  const handleMarkNotSent = useCallback(() => {
    resolveMutation.mutate('verified_not_sent');
  }, [resolveMutation]);

  const isResolving = resolveMutation.isPending;
  // We surface the backend's user-facing copy verbatim when present so
  // the executor controls the wording (and the action-type-specific
  // verification target). Fall back to a generic prompt if the field is
  // missing on older rows.
  const message =
    result?.error ??
    `We couldn't confirm whether this action completed. Verify in ${verificationLabel} and choose an outcome.`;

  const errorText = resolveMutation.error
    ? 'Something went wrong saving your choice. Please try again.'
    : null;

  return (
    <div
      className='rounded-md border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] p-3 flex flex-col gap-2'
      data-component='indeterminate-action-row'
      data-instance={`indeterminate-action-${action.id}`}
    >
      <p className='text-body-small text-[var(--color-text-primary)]'>
        {message}
      </p>
      <div className='flex flex-wrap items-center gap-2'>
        <Button
          size='sm'
          color='secondary'
          onClick={handleMarkSent}
          isDisabled={isResolving}
          isLoading={
            isResolving && resolveMutation.variables === 'verified_sent'
          }
          data-component='action-button'
          data-instance={`resolve-mark-sent-${action.id}`}
        >
          I verified – mark as sent
        </Button>
        <Button
          size='sm'
          color='secondary'
          onClick={handleMarkNotSent}
          isDisabled={isResolving}
          isLoading={
            isResolving && resolveMutation.variables === 'verified_not_sent'
          }
          data-component='action-button'
          data-instance={`resolve-mark-not-sent-${action.id}`}
        >
          I verified – mark as not sent (allow retry)
        </Button>
      </div>
      {errorText && (
        <p
          role='alert'
          className='text-body-small text-[var(--color-text-error-primary)]'
        >
          {errorText}
        </p>
      )}
    </div>
  );
}

export function IndeterminateResolveBanner({
  orgId,
  proposalId,
  actions,
  onResolved,
}: IndeterminateResolveBannerProps) {
  const indeterminateActions = useMemo(
    () =>
      actions.filter(
        a =>
          a.is_current_version !== false &&
          a.execution_result?.status === 'indeterminate'
      ),
    [actions]
  );

  if (indeterminateActions.length === 0) {
    return null;
  }

  const headlineCount = indeterminateActions.length;
  const headline =
    headlineCount === 1
      ? "An action's outcome needs your verification"
      : `${headlineCount} actions need your verification`;

  return (
    <div
      className='mx-3 my-2 rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-3 flex flex-col gap-3'
      data-component='indeterminate-resolve-banner'
      role='region'
      aria-label='Action verification required'
    >
      <div className='flex items-start gap-2'>
        <AlertTriangle
          className='size-5 text-fg-warning-secondary mt-0.5 shrink-0'
          aria-hidden='true'
        />
        <div className='flex flex-col gap-1'>
          <h3 className='text-body font-medium text-[var(--color-text-primary)]'>
            {headline}
          </h3>
          <p className='text-body-small text-[var(--color-text-secondary)]'>
            We couldn&apos;t confirm whether one or more side effects completed
            (the worker stopped mid-execution). Verify each one and mark it as
            sent or not sent. The proposal can&apos;t be re-approved until every
            verification is recorded.
          </p>
        </div>
      </div>
      <div className='flex flex-col gap-2'>
        {indeterminateActions.map(action => (
          <IndeterminateActionRow
            key={action.id}
            orgId={orgId}
            proposalId={proposalId}
            action={action}
            onResolved={onResolved}
          />
        ))}
      </div>
    </div>
  );
}
